import React from 'react';
import { useFitnessWizard } from '../../contexts/FitnessWizardContext';
import { ThemedButton } from '../ThemedButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MapPin, Plus, Trash2, CheckCircle2 } from 'lucide-react';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

const FACILITY_TYPES = [
  { value: 'leased', label: 'Leased Space' },
  { value: 'owned', label: 'Owned Building' },
  { value: 'home', label: 'Home-based' },
  { value: 'mobile', label: 'Mobile / Pop-up' }
];

const FitnessLocations = () => {
  const { formData, updateFormData, nextStep } = useFitnessWizard();

  const handleSubmit = (e) => {
    e.preventDefault();
    nextStep();
  };

  const handleLocationTypeChange = (type) => {
    updateFormData({ location_type: type });
    if (type === 'Single' && formData.locations.length > 1) {
      updateFormData({ locations: [formData.locations[0]] });
    }
  };

  const handleLocationChange = (index, field, value) => {
    const newLocations = [...formData.locations];
    newLocations[index] = { ...newLocations[index], [field]: value };
    updateFormData({ locations: newLocations });
  };

  const addLocation = () => {
    updateFormData({ 
      locations: [...formData.locations, { address: '', city: '', state: '', zip: '' }] 
    });
  };

  const removeLocation = (index) => {
    if (formData.locations.length > 1) {
      const newLocations = formData.locations.filter((_, i) => i !== index);
      updateFormData({ locations: newLocations });
    }
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-[#232323] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <MapPin className="h-6 w-6" />
          <div>
            <CardTitle className="text-xl">Locations & Facility Details</CardTitle>
            <CardDescription className="text-white/60">
              Tell us about your physical location(s)
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Single vs Multiple */}
          <div>
            <Label className="text-base font-semibold">Do you operate at a single location or multiple? *</Label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {['Single', 'Multiple'].map((type) => (
                <div
                  key={type}
                  data-testid={`location-type-${type.toLowerCase()}`}
                  onClick={() => handleLocationTypeChange(type)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                    formData.location_type === type
                      ? 'border-[#7B6FE8] bg-[#EEF0FF]'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className={`font-medium ${
                    formData.location_type === type ? 'text-[#1A1A1A]' : 'text-slate-700'
                  }`}>
                    {type} Location{type === 'Multiple' ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Location Addresses */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Physical Location Address(es) *</Label>
            {formData.locations.map((location, index) => (
              <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Location {index + 1}</span>
                  {formData.locations.length > 1 && (
                    <ThemedButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLocation(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </ThemedButton>
                  )}
                </div>
                <Input
                  data-testid={`location-${index}-address`}
                  value={location.address}
                  onChange={(e) => handleLocationChange(index, 'address', e.target.value)}
                  required
                  placeholder="Street Address"
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    data-testid={`location-${index}-city`}
                    value={location.city}
                    onChange={(e) => handleLocationChange(index, 'city', e.target.value)}
                    required
                    placeholder="City"
                  />
                  <select
                    data-testid={`location-${index}-state`}
                    value={location.state}
                    onChange={(e) => handleLocationChange(index, 'state', e.target.value)}
                    required
                    className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">State</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  <Input
                    data-testid={`location-${index}-zip`}
                    value={location.zip}
                    onChange={(e) => handleLocationChange(index, 'zip', e.target.value)}
                    required
                    placeholder="ZIP"
                  />
                </div>
              </div>
            ))}

            {formData.location_type === 'Multiple' && (
              <ThemedButton
                type="button"
                variant="outline"
                onClick={addLocation}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Location
              </ThemedButton>
            )}
          </div>

          {/* Facility Type */}
          <div>
            <Label className="text-base font-semibold">Facility Type *</Label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {FACILITY_TYPES.map((type) => (
                <div
                  key={type.value}
                  data-testid={`facility-type-${type.value}`}
                  onClick={() => updateFormData({ facility_type: type.value })}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.facility_type === type.value
                      ? 'border-[#4ECDC4] bg-[#EEF0FF]'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${
                      formData.facility_type === type.value ? 'text-[#1A1A1A]' : 'text-slate-700'
                    }`}>
                      {type.label}
                    </span>
                    {formData.facility_type === type.value && (
                      <CheckCircle2 className="h-5 w-5 text-[#4ECDC4]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Optional Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="square_footage">Square Footage (Optional)</Label>
              <Input
                id="square_footage"
                type="number"
                data-testid="square-footage-input"
                value={formData.square_footage}
                onChange={(e) => updateFormData({ square_footage: parseInt(e.target.value) || '' })}
                className="mt-1.5"
                placeholder="e.g., 2500"
              />
            </div>
            <div>
              <Label htmlFor="hours_of_operation">Hours of Operation (Optional)</Label>
              <Input
                id="hours_of_operation"
                data-testid="hours-input"
                value={formData.hours_of_operation}
                onChange={(e) => updateFormData({ hours_of_operation: e.target.value })}
                className="mt-1.5"
                placeholder="e.g., Mon-Fri 6am-9pm"
              />
            </div>
          </div>

          <ThemedButton 
            type="submit"
            data-testid="fitness-locations-next-btn"
            disabled={!formData.facility_type || formData.locations.some(l => !l.address || !l.city || !l.state || !l.zip)}
            variant="wizard" size="full"
          >
            Next: Membership & Participation
          </ThemedButton>
        </form>
      </CardContent>
    </Card>
  );
};

export default FitnessLocations;
